"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeRivalryResult = void 0;
const courseDataHelpers_1 = require("../Utils/courseDataHelpers");
const stablefordUtils_1 = require("./stablefordUtils");
const rivalryConfigUtils_1 = require("./rivalryConfigUtils");
/** Summation method: sum each rivalry team's best N scores (countingScores) for the round. */
const RivalrySummation = ({ leaderboardData, courseData, currentRound, leaderboardScoreFormat, eventData, }) => {
    const rivalry = (0, rivalryConfigUtils_1.resolveRivalryConfig)(eventData);
    if (!rivalry)
        return null;
    const countingScores = Number(rivalry.countingScores) || null;
    const teamAName = rivalry.teamA?.name ?? '';
    const teamBName = rivalry.teamB?.name ?? '';
    const roundPlayType = eventData?.rounds?.[currentRound - 1]?.playType;
    const stableford = (0, stablefordUtils_1.isStablefordPlayType)(roundPlayType);
    const holePoints = eventData?.rounds?.[currentRound - 1]?.holePoints ?? [];
    const isNet = leaderboardScoreFormat === 'net';
    /** Compute a single player's round total (toPar or stableford points) */
    const computePlayerScore = (item) => {
        const roundCourseID = eventData?.rounds?.[currentRound - 1]?.courseID;
        const currentCourseData = (0, courseDataHelpers_1.getCourseById)(courseData, roundCourseID);
        const teeSetData = (0, courseDataHelpers_1.getTeeSetFromCourse)(currentCourseData, item.playerInfo?.[0]?.teeSet);
        let toPar = 0;
        let totalPoints = 0;
        (item.roundScores?.[currentRound - 1]?.holeScores ?? []).forEach((holeScore) => {
            if (holeScore.playerScore !== 0 && holeScore.playerScore !== null) {
                toPar += holeScore.playerScore
                    - (isNet ? (holeScore.playerHcpAdj ?? 0) : 0)
                    - (teeSetData?.holes?.[holeScore.holeNumber - 1]?.par ?? 0);
                if (stableford) {
                    totalPoints += (0, stablefordUtils_1.calculateHoleStablefordPoints)(holeScore.playerScore, holeScore.par, holeScore.playerHcpAdj ?? 0, isNet, holePoints);
                }
            }
        });
        const holesCompleted = item.holesCompleted ?? 0;
        const confirmed = !!item.roundScores?.[currentRound - 1]?.confirmed;
        const submitted = !!item.roundScores?.[currentRound - 1]?.submitted;
        return { toPar, totalPoints, holesCompleted, confirmed, submitted };
    };
    /** Compute the rivalry team result for a given team name */
    const computeTeamResult = (teamName) => {
        const members = leaderboardData.filter((item) => (item.rivalryTeam ?? item.playerInfo?.[0]?.rivalryTeam) === teamName);
        if (members.length === 0)
            return null;
        const playerScores = members.map(computePlayerScore);
        // Sort: for stableford higher is better; for stroke lower (more negative) is better
        const sorted = stableford
            ? [...playerScores].sort((a, b) => b.totalPoints - a.totalPoints)
            : [...playerScores].sort((a, b) => a.toPar - b.toPar);
        const sliced = sorted.slice(0, countingScores ?? sorted.length);
        const totalToPar = stableford
            ? undefined
            : sliced.reduce((sum, s) => sum + s.toPar, 0);
        const totalPoints = stableford
            ? sliced.reduce((sum, s) => sum + s.totalPoints, 0)
            : undefined;
        const holesCompleted = Math.min(...sliced.map((s) => s.holesCompleted));
        const confirmed = sliced.every((s) => s.confirmed);
        const submitted = sliced.every((s) => s.submitted);
        return {
            teamName,
            toPar: totalToPar,
            totalPoints,
            holesCompleted,
            playerCount: members.length,
            countingScoresUsed: sliced.length,
            confirmed,
            submitted,
        };
    };
    const teamA = computeTeamResult(teamAName);
    const teamB = computeTeamResult(teamBName);
    let leader = null;
    let leadAmount = 0;
    if (teamA && teamB) {
        if (stableford) {
            const aPoints = teamA.totalPoints ?? 0;
            const bPoints = teamB.totalPoints ?? 0;
            if (aPoints !== bPoints) {
                leader = aPoints > bPoints ? teamAName : teamBName;
                leadAmount = Math.abs(aPoints - bPoints);
            }
        }
        else {
            const aScore = teamA.toPar ?? 0;
            const bScore = teamB.toPar ?? 0;
            if (aScore !== bScore) {
                leader = aScore < bScore ? teamAName : teamBName;
                leadAmount = Math.abs(aScore - bScore);
            }
        }
    }
    return { teamA, teamB, leader, leadAmount };
};
// Method dispatch map — add new methods here as the rivalry feature expands
const rivalryMethodMap = {
    summation: RivalrySummation,
};
const computeRivalryResult = (method, props) => {
    const fn = rivalryMethodMap[method] ?? RivalrySummation;
    return fn(props);
};
exports.computeRivalryResult = computeRivalryResult;
